/**
 * ConversationList - Lista de conversas com scroll infinito.
 */
import React from 'react';
import { ChatConversation } from '../hooks/types';
import { conversationDedupeKey } from '../../../services/repositories/helpers/ChatConversationMapper';
import ConversationItem from './ConversationItem';
import { EmptyConversations } from './EmptyConversations';
import { LoadingIndicator, EndOfListIndicator } from './ListIndicators';

interface ConversationListProps {
    filteredConversations: ChatConversation[];
    activeConversationId: string | null;
    activeConnectionId?: string | null;
    activeTab: string;
    isRefreshing?: boolean;
    isLoadingMore: boolean;
    hasMoreConversations: boolean;
    onScroll: (e: React.UIEvent<HTMLDivElement>) => void;
    onSelectConversation: (id: string, connectionId?: string | null) => void;
    resolveConversationStageName: (conv: ChatConversation) => string | null;
    getAssignedUser: (assignedTo: string | undefined) => { id?: string; name?: string; accessType?: string } | undefined;
    isFavorite: (id: string) => boolean;
    showUnreadOnly?: boolean;
}

export const ConversationList: React.FC<ConversationListProps> = ({
    filteredConversations,
    activeConversationId,
    activeConnectionId = null,
    activeTab,
    isRefreshing = false,
    isLoadingMore,
    hasMoreConversations,
    onScroll,
    onSelectConversation,
    resolveConversationStageName,
    getAssignedUser,
    isFavorite,
    showUnreadOnly = false,
}) => {
    const isEmpty = filteredConversations.length === 0;

    return (
        <nav
            id="chat-conversation-list"
            aria-label="Lista de conversas"
            className="relative min-h-0 flex-1 overflow-y-auto overscroll-y-contain bg-card custom-scrollbar"
            onScroll={onScroll}
        >
            {isRefreshing && filteredConversations.length > 0 && (
                <div className="absolute left-0 right-0 top-0 z-10 h-0.5 animate-pulse bg-gradient-primary" aria-hidden />
            )}

            {isEmpty ? (
                <EmptyConversations activeTab={activeTab} showUnreadOnly={showUnreadOnly} />
            ) : (
                <div className="flex flex-col">
                    <ul className="m-0 flex list-none flex-col p-0">
                        {filteredConversations.map((conv) => {
                            const assigned = getAssignedUser(conv.assignedTo);
                            const isActive = activeConversationId === conv.id
                                && (!activeConnectionId
                                    || !conv.connectionId
                                    || activeConnectionId === conv.connectionId);
                            return (
                                <li key={conversationDedupeKey(conv)} className="min-w-0">
                                    <ConversationItem
                                        conversation={conv}
                                        isActive={isActive}
                                        onClick={onSelectConversation}
                                        stageName={resolveConversationStageName(conv)}
                                        assignedUserName={assigned?.name}
                                        assignedUserId={assigned?.id ?? conv.assignedTo}
                                        isFavorite={isFavorite(conv.id)}
                                    />
                                </li>
                            );
                        })}
                    </ul>

                    {isLoadingMore && <LoadingIndicator />}
                    {!hasMoreConversations && filteredConversations.length > 0 && !isLoadingMore && (
                        <EndOfListIndicator />
                    )}
                </div>
            )}
        </nav>
    );
};
